// Lista tecnici con KPI rapidi e creazione
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Users, Search, Plus, Link2, Copy, CheckCheck } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Select'
import { Modal } from '../../components/ui/Modal'
import { Avatar } from '../../components/ui/Avatar'
import { Badge } from '../../components/ui/Badge'
import { EmptyState } from '../../components/ui/EmptyState'
import { CardSkeleton } from '../../components/ui/Skeleton'
import api from '../../lib/api'

interface TechnicianListItem {
  id: string
  name?: string
  firstName?: string
  lastName?: string
  email: string
  phone?: string
  role: string
  isOnline?: boolean
  isCheckedIn?: boolean
  openTickets?: number
  completedThisMonth?: number
}

const schema = z.object({
  firstName: z.string().min(1, 'Nome obbligatorio'),
  lastName: z.string().min(1, 'Cognome obbligatorio'),
  email: z.string().email('Email non valida'),
  phone: z.string().optional(),
  role: z.enum(['technician', 'team_leader']),
})

type FormData = z.infer<typeof schema>

function getTechnicianName(t: TechnicianListItem): string {
  if (t.name) return t.name
  if (t.firstName || t.lastName) return `${t.firstName ?? ''} ${t.lastName ?? ''}`.trim()
  return t.email
}

export function TechniciansPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [magicLink, setMagicLink] = useState<{ email: string; url: string } | null>(null)
  const [copied, setCopied] = useState(false)

  const { data: technicians = [], isLoading } = useQuery({
    queryKey: ['technicians'],
    queryFn: async () => {
      try {
        const res = await api.get<TechnicianListItem[]>('/technicians')
        return res.data ?? []
      } catch {
        return [] as TechnicianListItem[]
      }
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { firstName: '', lastName: '', email: '', phone: '', role: 'technician' },
  })

  const createMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const res = await api.post<{ id: string; email: string; magicUrl?: string }>('/technicians', data)
      return res.data
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['technicians'] })
      setFormOpen(false)
      reset()
      if (data?.magicUrl) {
        setMagicLink({ email: data.email, url: data.magicUrl })
      }
    },
  })

  function copyLink() {
    if (!magicLink) return
    void navigator.clipboard.writeText(magicLink.url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const filtered = technicians.filter((t) => {
    const name = getTechnicianName(t).toLowerCase()
    const email = (t.email ?? '').toLowerCase()
    const q = search.toLowerCase()
    return name.includes(q) || email.includes(q)
  })

  return (
    <div className="space-y-4">
      <PageHeader
        title="Tecnici"
        subtitle={`${technicians.length} tecnici registrati`}
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => { reset(); setFormOpen(true) }}>
            Nuovo tecnico
          </Button>
        }
      />

      <div className="max-w-sm">
        <Input
          placeholder="Cerca tecnico..."
          leftIcon={<Search className="h-4 w-4" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Users className="h-12 w-12" />}
          title="Nessun tecnico trovato"
          description={search ? 'Nessun tecnico corrisponde alla ricerca' : 'Aggiungi il primo tecnico con il bottone in alto'}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((tech) => (
            <button
              key={tech.id}
              className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4 text-left hover:border-[var(--fc-primary)] hover:shadow-[var(--fc-shadow)] transition-all"
              onClick={() => navigate(`/technicians/${tech.id}`)}
              aria-label={`Vedi profilo di ${getTechnicianName(tech)}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <Avatar name={getTechnicianName(tech)} size="md" online={tech.isOnline ?? false} />
                <div className="min-w-0">
                  <p className="font-semibold text-[var(--fc-foreground)] truncate">{getTechnicianName(tech)}</p>
                  <p className="text-xs text-[var(--fc-foreground-muted)] truncate">{tech.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant={tech.isCheckedIn ? 'success' : 'default'} dot>
                  {tech.isCheckedIn ? 'In campo' : 'Non disponibile'}
                </Badge>
                {(tech.openTickets !== undefined || tech.completedThisMonth !== undefined) && (
                  <span className="text-xs text-[var(--fc-foreground-muted)]">
                    {tech.openTickets ?? 0} aperti · {tech.completedThisMonth ?? 0} completati/mese
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Modal creazione tecnico */}
      <Modal
        open={formOpen}
        onClose={() => { setFormOpen(false); reset() }}
        title="Nuovo tecnico"
        size="md"
      >
        <form onSubmit={handleSubmit((data) => createMutation.mutate(data))} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Nome *"
              {...register('firstName')}
              error={errors.firstName?.message}
              placeholder="Mario"
            />
            <Input
              label="Cognome *"
              {...register('lastName')}
              error={errors.lastName?.message}
              placeholder="Rossi"
            />
          </div>
          <Input
            label="Email *"
            type="email"
            {...register('email')}
            error={errors.email?.message}
            placeholder="mario.rossi@esempio.it"
          />
          <Input
            label="Telefono"
            type="tel"
            {...register('phone')}
            placeholder="+39 333 1234567"
          />
          <Select
            label="Ruolo"
            options={[
              { value: 'technician', label: 'Tecnico' },
              { value: 'team_leader', label: 'Team Leader' },
            ]}
            value={watch('role')}
            onValueChange={(v) => setValue('role', v as FormData['role'])}
          />
          <div className="rounded-lg bg-[color-mix(in_srgb,var(--fc-primary)_8%,transparent)] border border-[color-mix(in_srgb,var(--fc-primary)_20%,transparent)] p-3 text-sm text-[var(--fc-foreground-muted)]">
            <span className="font-medium text-[var(--fc-primary)]">Accesso senza password</span> — dopo la creazione riceverai un link di invito da condividere con il tecnico. Al primo accesso potrà impostare la propria password.
          </div>
          {createMutation.isError && (
            <p className="text-sm text-[var(--fc-danger)]">
              Errore durante la creazione. Verifica che l'email non sia già in uso.
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={() => { setFormOpen(false); reset() }}>
              Annulla
            </Button>
            <Button type="submit" disabled={isSubmitting || createMutation.isPending}>
              {createMutation.isPending ? 'Creazione...' : 'Crea tecnico'}
            </Button>
          </div>
        </form>
      </Modal>
      {/* Modal link di accesso */}
      <Modal
        open={!!magicLink}
        onClose={() => setMagicLink(null)}
        title="Tecnico creato"
        size="md"
      >
        {magicLink && (
          <div className="space-y-4">
            <p className="text-sm text-[var(--fc-foreground-muted)]">
              Tecnico <strong className="text-[var(--fc-foreground)]">{magicLink.email}</strong> creato con successo.
              Condividi questo link di primo accesso — è valido 7 giorni.
            </p>
            <div className="flex items-center gap-2 rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface-overlay)] p-3">
              <Link2 className="h-4 w-4 shrink-0 text-[var(--fc-primary)]" />
              <span className="flex-1 truncate text-xs font-mono text-[var(--fc-foreground)]">{magicLink.url}</span>
              <button
                onClick={copyLink}
                className="shrink-0 rounded p-1 hover:bg-[var(--fc-surface)] transition-colors"
                title="Copia link"
              >
                {copied ? <CheckCheck className="h-4 w-4 text-[var(--fc-success)]" /> : <Copy className="h-4 w-4 text-[var(--fc-foreground-muted)]" />}
              </button>
            </div>
            <p className="text-xs text-[var(--fc-foreground-subtle)]">
              Il tecnico aprirà questo link nel browser, imposterà la propria password e accederà all'app mobile con email + password.
            </p>
            <div className="flex justify-end">
              <Button onClick={() => setMagicLink(null)}>Chiudi</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
